import { Injectable } from "@angular/core";
import { TenantGrant } from "../models/http/tenant/tenant-grant.model";
import { ROLE_LEVELS, ROLE_PERMISSIONS, RolePermissionKey } from "../models/ui/authorization.model";
import { Role } from "../models/ui/role.model";
import { UserStateService } from "./state/user-state.service";

@Injectable({
	providedIn: "root"
})
export class AuthorizationService {
	constructor(private _userStateService: UserStateService) {}

	/**
	 * Checks whether the active tenant grant and minimum role permit visibility.
	 * @param grant The tenant visibility grant required for the item.
	 * @param minimumRole The optional minimum role required to see the item.
	 * @returns `true` when the user can see the item; otherwise, `false`.
	 */
	public canSee(grant: TenantGrant, minimumRole?: Role): boolean {
		return this.hasGrant(grant) && (!minimumRole || this._hasMinimumRole(minimumRole));
	}

	/**
	 * Checks whether the current user has the specified tenant grant.
	 * @param grant The tenant grant to check.
	 * @returns `true` when the grant is present; otherwise, `false`.
	 */
	public hasGrant(grant: TenantGrant): boolean {
		return this._userStateService.userInfo.grants?.includes(grant) ?? false;
	}

	/**
	 * Checks whether the current user has the specified role.
	 * @param role The role to check.
	 * @returns `true` when the user has the role; otherwise, `false`.
	 */
	public hasRole(role: Role): boolean {
		return this._getRole() === role;
	}

	/**
	 * Checks whether the current user's role allows an action (upload, rename, delete...).
	 * @param permission The action permission from the role matrix.
	 * @returns `true` when the role grants the permission; otherwise, `false`.
	 */
	public hasPermission(permission: RolePermissionKey): boolean {
		const role = this._getRole();
		return role !== undefined && ROLE_PERMISSIONS[role].includes(permission);
	}

	private _hasMinimumRole(minimumRole: Role): boolean {
		const currentRole = this._getRole();
		return currentRole !== undefined && ROLE_LEVELS[currentRole] >= ROLE_LEVELS[minimumRole];
	}

	private _getRole(): Role | undefined {
		return Object.values(Role).find(role => role === this._userStateService.userInfo.role);
	}
}
